import {
	type BulkInviteFailureResponse,
	type BulkInviteResponse,
	type BulkInviteSuccessResponse,
	type InviteApiFailureResponse,
	type InviteApiRequest,
	type InviteApiSuccessResponse,
	type InvitedUser,
	isUserWithEmail,
	type InviteCapabilitiesObject,
	type InviteCapabilitiesEntry,
	type InviteOptionalEmailApiRequest,
	CollectionType,
	type Collections,
	type GetCollectionsRequest,
	type EntitlementsResponse,
} from '../types';
import {
	type GetDirectAccessSettingFailureResponseValue,
	type GetDirectAccessSettingRequest,
	type GetDirectAccessSettingResponse,
	type GetDirectAccessSettingSuccessResponseValue,
	type UpdateDirectAccessSettingRequest,
	type UpdateDirectAccessSettingResponse,
} from '../components/ViralSettings/types';

import {
	BULK_INVITE_URL,
	DEFAULT_STARGATE_SERVICE_URL,
	INVITE_CAPABILITIES,
	DIRECT_ACCESS_URL,
	BULK_INVITE_URL_V3,
	GRAPHQL_GATEWAY_URL,
} from './common';
import {
	getProductTitleFromAri,
	getProductIdFromResource,
	tryCacheWithClear,
	resolveError,
} from '../utils';

import {
	FetchError,
	fetchJson,
	postJson,
	postRequest,
	type RecaptchaOptions,
	RespCodes,
} from './helpers';
import {
	entitlementsQuery,
	getHasTeamworkCollection,
	twcProductKeys,
} from '../experiments/twcForCoreInvites/CollectionsContext';

export interface InviteApiClient {
	inviteUsers(request: InviteApiRequest): Promise<BulkInviteResponse>;
	inviteUsersV3(request: InviteApiRequest): Promise<BulkInviteResponse>;
	inviteCapabilities(cloudId: string): Promise<InviteCapabilitiesObject>;
	getDirectAccessSetting(
		request: GetDirectAccessSettingRequest,
	): Promise<GetDirectAccessSettingResponse>;
	updateDirectAccessSettings(
		request: UpdateDirectAccessSettingRequest,
	): Promise<UpdateDirectAccessSettingResponse>;
	getCollections(request: GetCollectionsRequest): Promise<Collections>;
}

const LICENCE_EXCEEDED_ARI_REGEX = /(\[ari:.*?)(\/.*)(\])/;

export const INVITE_API_MAX_USERS = 10;

export class InviteApiClientImpl implements InviteApiClient {
	directAccessSettingsCache: Map<
		string,
		GetDirectAccessSettingSuccessResponseValue | GetDirectAccessSettingFailureResponseValue
	>;
	constructor() {
		this.directAccessSettingsCache = new Map();
	}

	// only emails are supported for invites - VULN-1024816
	public async inviteUsers(
		request: InviteApiRequest,
		recaptchaOptions?: RecaptchaOptions,
	): Promise<BulkInviteResponse> {
		return this.inviteUsersCommon(
			request,
			DEFAULT_STARGATE_SERVICE_URL + BULK_INVITE_URL,
			recaptchaOptions,
		);
	}
	// support both aaid and emails, users have to be in the same org, using v3/invitations/invite
	public async inviteUsersV3(
		request: InviteApiRequest,
		recaptchaOptions?: RecaptchaOptions,
	): Promise<BulkInviteResponse> {
		return this.inviteUsersCommon(
			request,
			DEFAULT_STARGATE_SERVICE_URL + BULK_INVITE_URL_V3,
			recaptchaOptions,
		);
	}
	private async inviteUsersCommon(
		request: InviteApiRequest | InviteOptionalEmailApiRequest,
		url: string,
		recaptchaOptions?: RecaptchaOptions,
	): Promise<BulkInviteResponse> {
		try {
			const inviteApiResponse = await postJson<Array<InviteApiSuccessResponse>>(
				url,
				'inviteUsers',
				request,
				recaptchaOptions,
			);

			return this.handleInviteUsersSuccessResponse(inviteApiResponse);
		} catch (err) {
			if (err instanceof FetchError) {
				const inviteApiResponse = err.responseJson;
				if (inviteApiResponse) {
					const failureResponse: InviteApiFailureResponse = {
						status: err.responseCode,
						...inviteApiResponse,
					};
					return this.handleInviteUsersFailureResponse(failureResponse);
				}
			}
			throw err;
		}
	}
	public clearDESCache = () => {
		this.directAccessSettingsCache.clear();
	};

	public async inviteCapabilities(cloudId: string): Promise<InviteCapabilitiesObject> {
		const resource = `ari:cloud:platform::site/${cloudId}`;
		const url = `${DEFAULT_STARGATE_SERVICE_URL}${INVITE_CAPABILITIES}?resource=${resource}`;

		const capabilitiesResponse = await fetchJson<InviteCapabilitiesEntry[]>(
			url,
			'inviteCapabilities',
		);
		return this.handleCapabilitiesResponse(capabilitiesResponse);
	}

	private getProductDetails = (productArray: string[]) =>
		productArray.map((p) => ({
			name: getProductTitleFromAri(p),
			id: getProductIdFromResource(p) || '',
			ari: p,
		}));

	private getProductDetailsWithDomains = (productRecord: Record<string, string[] | undefined>) =>
		Object.keys(productRecord).map((p) => ({
			name: getProductTitleFromAri(p),
			id: getProductIdFromResource(p) || '',
			ari: p,
			domains: productRecord[p],
		}));

	private handleCapabilitiesResponse(
		response: InviteCapabilitiesEntry[],
	): InviteCapabilitiesObject {
		const directInvite: Record<string, string[] | undefined> = {};
		const invitePendingApproval = new Set<string>();

		if (Array.isArray(response)) {
			response.forEach((entry) => {
				if ('NONE' !== entry.directInvite.mode) {
					directInvite[entry.resourceARI] = entry.directInvite.domains;
				}

				if (
					!invitePendingApproval.has(entry.resourceARI) &&
					'NONE' !== entry.invitePendingApproval.mode
				) {
					invitePendingApproval.add(entry.resourceARI);
				}
			});
		}

		return {
			directInvite: this.getProductDetailsWithDomains(directInvite),
			invitePendingApproval: this.getProductDetails(Array.from(invitePendingApproval)),
		};
	}

	private cleanErrorMessage(message: string) {
		// Remove AIDs from ARIs in error messages to better group in logs
		// - Licence Exceeded for resources:[ari:cloud:confluence::site/aaaaaaaa-64d4-469e-91df-bbbbbbbbbbbb]
		return message ? message.replace(LICENCE_EXCEEDED_ARI_REGEX, '$1$3') : '';
	}

	private handleInviteUsersFailureResponse(
		errorResponse: InviteApiFailureResponse,
	): BulkInviteFailureResponse {
		return {
			failure: {
				...errorResponse,
				message: this.cleanErrorMessage(errorResponse.message),
			},
		};
	}

	private hasUser(users: InvitedUser[], invitedUser: InvitedUser): boolean {
		return users.some(
			(user: InvitedUser) =>
				(isUserWithEmail(user) &&
					isUserWithEmail(invitedUser) &&
					user.email === invitedUser.email) ||
				user.id === invitedUser.id,
		);
	}

	private handleInviteUsersSuccessResponse(
		response: InviteApiSuccessResponse[],
	): BulkInviteSuccessResponse {
		return response.reduce<BulkInviteSuccessResponse>(
			(accumulator, userResponse) => {
				const user = isUserWithEmail(userResponse)
					? {
							id: userResponse.id,
							email: userResponse.email,
						}
					: { id: userResponse.id };

				Object.keys(userResponse.results).forEach((resource) => {
					switch (userResponse.results[resource]) {
						case 'INVITED':
						case 'USER_EXISTS':
							if (!this.hasUser(accumulator.invited, user)) {
								accumulator.invited.push({
									...user,
									results: userResponse.results,
								});
							}
							break;
						case 'INVITED_PENDING_APPROVAL':
						case 'PENDING_INVITE_EXISTS':
							if (!this.hasUser(accumulator.accessRequested, user)) {
								accumulator.accessRequested.push({
									...user,
									results: userResponse.results,
								});
							}
							break;
						case 'NOT_INVITED':
						case 'ERROR':
							accumulator.error.push({
								...user,
								error: userResponse.errorReasons[resource],
							});
							break;
					}
				});
				return accumulator;
			},
			{
				invited: [],
				accessRequested: [],
				error: [],
			},
		);
	}

	public async updateDirectAccessSettings({
		product,
		cloudId,
		location,
		domains,
	}: UpdateDirectAccessSettingRequest): Promise<UpdateDirectAccessSettingResponse> {
		const resource = `ari:cloud:${product}::site/${cloudId}`;
		const url = `${DEFAULT_STARGATE_SERVICE_URL}${DIRECT_ACCESS_URL}`;
		const request = {
			resource,
			domains,
			location,
			action: 'ACCEPT',
			setting: 'DIRECT_ACCESS',
		};
		await postRequest(url, 'updateDirectAccessSettings', request);
		return { success: true, updateDirectAccessSettings: true };
	}

	public async getDirectAccessSetting(
		request: GetDirectAccessSettingRequest,
	): Promise<GetDirectAccessSettingResponse> {
		const performFetch = () => {
			const url = `${DEFAULT_STARGATE_SERVICE_URL}${DIRECT_ACCESS_URL}`;
			const queryParams = `resource=${request.productAri}&domain=${
				request.domain
			}&setting=${request.setting}${request.roleAri ? '&role=' + request.roleAri : ''}`;

			return fetchJson<GetDirectAccessSettingSuccessResponseValue>(
				`${url}?${queryParams}`,
				'getDirectAccessSetting',
			);
		};

		const cacheValue = this.directAccessSettingsCache.get(request.domain);
		if (cacheValue) {
			return { response: cacheValue, cached: true };
		}
		try {
			const responseJson = await performFetch();
			tryCacheWithClear(this.directAccessSettingsCache, request.domain, responseJson);
			return { response: responseJson, cached: false };
		} catch (err) {
			const { responseCode, errorMessage } = resolveError(err);
			if (
				responseCode &&
				responseCode >= RespCodes.BAD_REQUESTS &&
				responseCode <= RespCodes.CLIENT_CLOSED_REQUEST
			) {
				const failResponseJson: GetDirectAccessSettingFailureResponseValue = {
					domain: request.domain,
					errorMessage,
				};
				tryCacheWithClear(this.directAccessSettingsCache, request.domain, failResponseJson);
			}
			throw err;
		}
	}

	public async getCollections({ cloudId }: GetCollectionsRequest): Promise<Collections> {
		const url = `${DEFAULT_STARGATE_SERVICE_URL}${GRAPHQL_GATEWAY_URL}`;
		const collectionsResponse = await fetchJson<EntitlementsResponse>(url, 'collections', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({
				query: entitlementsQuery,
				variables: {
					cloudId,
					skip: false,
				},
			}),
		});
		const hasTeamworkCollection = getHasTeamworkCollection(collectionsResponse.data);
		return {
			[CollectionType.TEAMWORK_COLLECTION]: {
				isEnabled: hasTeamworkCollection,
				products: hasTeamworkCollection ? twcProductKeys : [],
			},
		};
	}
}
